import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCartStore } from "./store/cartStore";
import { useTranslation } from "react-i18next";
import { useAuth } from "./AuthContext";

export default function Cart() {
  const { t, i18n } = useTranslation();
  const { user, openLoginModal } = useAuth();
  const navigate = useNavigate();
  const items = useCartStore((state) => state.items);
  const total = useCartStore((state) => state.total);
  const removeItem = useCartStore((state) => state.removeItem);
  const updateQuantity = useCartStore((state) => state.updateQuantity);

  if (items.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="text-6xl mb-4">🛒</div>
        <h1 className="text-4xl font-bold mb-4">{t('cart.title')}</h1>
        <p className="text-xl text-raya-text-secondary mb-8">
          {t('cart.empty')}
        </p>
        <Link to="/products">
          <button className="bg-raya-text-primary border border-raya-text-primary hover:border-raya-accent hover:text-raya-accent text-raya-bg px-8 py-3 rounded-lg transition transform hover:scale-105">
            {t('cart.continue')}
          </button>
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Progress Indicator */}
      <div className="mb-8">
        <div className="flex items-center justify-between max-w-md mx-auto">
          <div className="flex flex-col items-center progress-step active">
            <div className="w-10 h-10 rounded-full bg-raya-accent text-raya-bg flex items-center justify-center font-bold mb-2 shadow-lg">
              1
            </div>
            <span className="text-sm font-semibold text-raya-accent">{t('cart.stepCart')}</span>
          </div>
          <div className="flex flex-col items-center progress-step">
            <div className="w-10 h-10 rounded-full bg-raya-section text-raya-text-tertiary flex items-center justify-center font-bold mb-2">
              2
            </div>
            <span className="text-sm text-raya-text-tertiary">{t('cart.stepCheckout')}</span>
          </div>
          <div className="flex flex-col items-center progress-step">
            <div className="w-10 h-10 rounded-full bg-raya-section text-raya-text-tertiary flex items-center justify-center font-bold mb-2">
              3
            </div>
            <span className="text-sm text-raya-text-tertiary">{t('cart.stepPayment')}</span>
          </div>
        </div>
      </div>

      <h1 className="text-4xl font-bold mb-8">
        <span className="bg-gradient-premium bg-clip-text text-transparent">
          {t('cart.title')}
        </span>
      </h1>

      <div className="space-y-4">
        {items.map((item, index) => (
          <div
            key={item.id}
            className="bg-raya-card rounded-lg p-6 flex justify-between items-center shadow-md hover:shadow-premium transition-all animate-fade-in-up"
            style={{ animationDelay: `${index * 0.05}s` }}
          >
            <div className="flex items-center space-x-4 flex-grow">
              <div className="image-zoom-container rounded-lg overflow-hidden">
                {item.image ? (
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-20 h-20 object-cover image-zoom rounded"
                  />
                ) : (
                  <span className="text-4xl">📦</span>
                )}
              </div>
              <div>
                <h3 className="font-semibold text-lg">
                  {i18n.language === 'am' && item.name_am ? item.name_am : item.name}
                </h3>
                <p className="text-raya-text-secondary">
                  <span className="bg-gradient-to-r from-raya-accent to-raya-accent-hover bg-clip-text text-transparent font-semibold">
                    ${parseFloat(item.price).toFixed(2)}
                  </span>
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <div className="flex items-center border-2 border-raya-accent/50 rounded-lg overflow-hidden">
                <button
                  onClick={() =>
                    updateQuantity(item.id, Math.max(1, item.quantity - 1))
                  }
                  className="px-4 py-2 hover:bg-raya-accent hover:text-raya-bg transition font-bold"
                  aria-label="Decrease quantity"
                >
                  −
                </button>
                <span className="px-6 py-2 bg-raya-bg font-semibold">{item.quantity}</span>
                <button
                  onClick={() => updateQuantity(item.id, item.quantity + 1)}
                  className="px-4 py-2 hover:bg-raya-accent hover:text-raya-bg transition font-bold"
                  aria-label="Increase quantity"
                >
                  +
                </button>
              </div>

              <p className="font-bold min-w-24 text-right text-lg">
                ${(item.price * item.quantity).toFixed(2)}
              </p>

              <button
                onClick={() => removeItem(item.id)}
                className="text-red-500 hover:text-red-700 font-semibold transition transform hover:scale-110"
                title={t('cart.remove')}
                aria-label={`${t('cart.remove')} ${i18n.language === 'am' && item.name_am ? item.name_am : item.name}`}
              >
                🗑️
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 bg-raya-card rounded-lg p-6 shadow-premium">
        <div className="flex justify-between items-center mb-6 pb-4 border-b border-raya-border">
          <span className="text-xl font-semibold">{t('cart.subtotal')}</span>
          <span className="text-xl">${total.toFixed(2)}</span>
        </div>
        <div className="flex justify-between items-center mb-6 pb-4 border-b border-raya-border">
          <span className="text-xl font-semibold">{t('cart.shipping')}</span>
          <span className="text-xl text-green-600">{t('cart.free')}</span>
        </div>
        <div className="flex justify-between items-center mb-6">
          <span className="text-2xl font-bold">{t('cart.total')}</span>
          <span className="text-3xl font-bold bg-gradient-to-r from-raya-accent to-raya-accent-hover bg-clip-text text-transparent">
            ${total.toFixed(2)}
          </span>
        </div>

        <button 
          onClick={() => {
            if (user) {
              navigate("/checkout");
            } else {
              openLoginModal();
            }
          }}
          className="w-full bg-gradient-to-r from-raya-accent to-raya-accent-hover hover:from-raya-accent-hover hover:to-raya-accent text-raya-bg py-4 rounded-lg font-semibold transition transform hover:scale-105 hover:-translate-y-1 shadow-lg text-lg"
        >
          {t('cart.checkout')} →
        </button>

        <Link to="/products">
          <button className="w-full mt-3 border-2 border-raya-accent text-raya-accent py-3 rounded-lg font-semibold hover:bg-raya-accent-light transition">
            {t('cart.continue')}
          </button>
        </Link>
      </div>
    </div>
  );
}
